import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Badge, ProgressBar } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const TakeSurvey = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const journeyContext = location.state?.journeyContext;
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});

  const totalQuestions = useMemo(() => survey?.questions?.length || 0, [survey]);
  const answeredCount = useMemo(() => {
    if (!survey || !survey.questions) {
      return 0;
    }
    return survey.questions.filter((question) => {
      const answer = responses[question.id];
      if (Array.isArray(answer)) {
        return answer.length > 0;
      }
      if (typeof answer === 'string') {
        return answer.trim().length > 0;
      }
      return Boolean(answer);
    }).length;
  }, [survey, responses]);

  const requiredTotals = useMemo(() => {
    if (!survey || !survey.questions) {
      return { total: 0, answered: 0 };
    }
    const requiredQuestions = survey.questions.filter((question) => question.is_required);
    const answeredRequired = requiredQuestions.filter((question) => {
      const answer = responses[question.id];
      if (Array.isArray(answer)) {
        return answer.length > 0;
      }
      if (typeof answer === 'string') {
        return answer.trim().length > 0;
      }
      return Boolean(answer);
    }).length;
    return { total: requiredQuestions.length, answered: answeredRequired };
  }, [survey, responses]);

  const answerProgress = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;
  const requiredProgress = requiredTotals.total ? Math.round((requiredTotals.answered / requiredTotals.total) * 100) : 0;

  const handleBack = () => {
    if (journeyContext) {
      navigate(`/user/learning-journeys/${journeyContext.journeyId}`);
      return;
    }
    navigate('/user/surveys');
  };

  useEffect(() => {
    fetchSurvey();
  }, [id]);

  const fetchSurvey = async () => {
    try {
      setLoading(true);
      const response = await api.surveys.getForResponse(id);
      const parseOptions = (rawOptions) => {
        if (!rawOptions) {
          return [];
        }
        if (Array.isArray(rawOptions)) {
          return rawOptions;
        }
        if (typeof rawOptions === 'string') {
          try {
            const parsed = JSON.parse(rawOptions);
            return Array.isArray(parsed) ? parsed : [];
          } catch (error) {
            return rawOptions.split('\n').map((item) => item.trim()).filter(Boolean);
          }
        }
        return [];
      };

      const surveyData = response.data.data;
      const normalizedQuestions = (surveyData.questions || []).map((question) => ({
        ...question,
        options: parseOptions(question.options)
      }));

      setSurvey({ ...surveyData, questions: normalizedQuestions });

      const initialResponses = {};
      normalizedQuestions.forEach((question) => {
        initialResponses[question.id] = question.question_type === 'checkbox' ? [] : '';
      });
      setResponses(initialResponses);
    } catch (error) {
      console.error('Error fetching survey:', error);
      toast.error(error.response?.data?.message || 'Failed to load survey');
      navigate('/user/surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleRadioChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
    if (errors[questionId]) {
      setErrors(prev => ({ ...prev, [questionId]: '' }));
    }
  };

  const handleCheckboxChange = (questionId, value) => {
    setResponses(prev => {
      const current = prev[questionId] || [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [questionId]: updated };
    });
    if (errors[questionId]) {
      setErrors(prev => ({ ...prev, [questionId]: '' }));
    }
  };

  const handleTextChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
    if (errors[questionId]) {
      setErrors(prev => ({ ...prev, [questionId]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    survey.questions.forEach(question => {
      if (question.is_required) {
        const answer = responses[question.id];
        if (!answer || (Array.isArray(answer) && answer.length === 0) || answer.trim?.() === '') {
          newErrors[question.id] = 'This question is required';
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please answer all required questions');
      return;
    }

    try {
      setSubmitting(true);
      await api.surveys.submitResponse({
        survey_id: id,
        responses
      });
      if (journeyContext) {
        try {
          await api.learningJourneys.completeStep(
            journeyContext.journeyId,
            journeyContext.stepId,
            { force_complete: true }
          );
        } catch (journeyError) {
          toast.warning(journeyError.response?.data?.message || 'Survey saved; journey progress will refresh shortly');
        }
      }
      toast.success('Survey submitted successfully!');
      handleBack();
    } catch (error) {
      console.error('Error submitting survey:', error);
      toast.error(error.response?.data?.message || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question, index) => {
    const hasError = !!errors[question.id];
    const options = Array.isArray(question.options) ? question.options : [];
 
    return (
      <Card key={question.id} className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
            <div className="d-flex align-items-start">
              <div
                className="d-flex align-items-center justify-content-center me-3"
                style={{
                  minWidth: '38px',
                  height: '38px',
                  background: 'linear-gradient(135deg, #28a74515, #17a2b815)',
                  borderRadius: '12px',
                  fontWeight: '700',
                  color: '#28a745'
                }}
              >
                {index + 1}
              </div>
              <div>
                <h6 className="mb-2 fw-semibold text-dark">
                  {question.question_text}
                  {question.is_required && <span className="text-danger ms-1">*</span>}
                </h6>
              </div>
            </div>
          </div>

          {question.question_type === 'radio' && (
            <div className="ps-3">
              {options.length === 0 ? (
                <Alert variant="light" className="border">
                  Options are not available for this question.
                </Alert>
              ) : (
                options.map((option, idx) => (
                  <Form.Check
                    key={idx}
                    type="radio"
                    id={`q${question.id}-opt${idx}`}
                    label={option}
                    name={`question-${question.id}`}
                    value={option}
                    checked={responses[question.id] === option}
                    onChange={(e) => handleRadioChange(question.id, e.target.value)}
                    isInvalid={hasError}
                    className="mb-2"
                  />
                ))
              )}
            </div>
          )}

          {question.question_type === 'checkbox' && (
            <div className="ps-3">
              {options.length === 0 ? (
                <Alert variant="light" className="border">
                  Options are not available for this question.
                </Alert>
              ) : (
                options.map((option, idx) => (
                  <Form.Check
                    key={idx}
                    type="checkbox"
                    id={`q${question.id}-opt${idx}`}
                    label={option}
                    value={option}
                    checked={(responses[question.id] || []).includes(option)}
                    onChange={(e) => handleCheckboxChange(question.id, e.target.value)}
                    isInvalid={hasError}
                    className="mb-2"
                  />
                ))
              )}
            </div>
          )}

          {question.question_type === 'textarea' && (
            <div className="ps-3">
              <Form.Control
                as="textarea"
                rows={4}
                value={responses[question.id] || ''}
                onChange={(e) => handleTextChange(question.id, e.target.value)}
                isInvalid={hasError}
                placeholder="Share your thoughts here..."
              />
            </div>
          )}

          {hasError && (
            <div className="ps-3 mt-2">
              <small className="text-danger fw-semibold">{errors[question.id]}</small>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container fluid className="p-0">
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="text-muted mt-3">Loading survey...</p>
        </div>
      </Container>
    );
  }

  if (!survey) {
    return (
      <div className="bg-light min-vh-100 d-flex align-items-center justify-content-center">
        <Alert variant="warning" className="shadow-sm border-0">
          Survey is not available at the moment. Please return to the surveys list and try again.
        </Alert>
      </div>
    );
  }

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}${imagePath}`;
  };

  const hasQuestions = Array.isArray(survey.questions) && survey.questions.length > 0;
  const surveyImageUrl = getImageUrl(survey.image_path);
  const surveyTypeVariant = survey.survey_type === 'global' ? 'primary' : 'success';
  const surveyTypeLabel = survey.survey_type === 'global' ? 'Global Survey' : 'Organization Survey';

  return (
    <div className="bg-light min-vh-100 py-4">
      <Container style={{ maxWidth: '960px' }}>
        <div className="rounded-4 shadow-sm mb-4 overflow-hidden">
          {surveyImageUrl && (
            <div style={{ maxHeight: '240px', overflow: 'hidden' }}>
              <img
                src={surveyImageUrl}
                alt={survey.title}
                className="w-100"
                style={{ objectFit: 'cover', height: '240px' }}
              />
            </div>
          )}
          <div
            className="p-4 text-white"
            style={{
              background: 'linear-gradient(135deg, rgba(40,167,69,0.95), rgba(13,110,253,0.85))'
            }}
          >
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
              <div className="d-flex align-items-start gap-3">
                <Button
                  variant="light"
                  className="rounded-circle shadow-sm"
                  style={{ width: '42px', height: '42px' }}
                  onClick={handleBack}
                >
                  <i className="fas fa-arrow-left"></i>
                </Button>
                <div>
                  <h3 className="fw-bold mb-2">{survey.title}</h3>
                  <div className="d-flex flex-wrap align-items-center gap-2 text-white-50 small">
                    <Badge bg={surveyTypeVariant} className="shadow-sm">
                      {surveyTypeLabel}
                    </Badge>
                    <span>
                      <i className="fas fa-question-circle me-1"></i>
                      {totalQuestions} {totalQuestions === 1 ? 'question' : 'questions'}
                    </span>
                    {survey.organization && (
                      <span>
                        <i className="fas fa-building me-1"></i>
                        {survey.organization.name}
                      </span>
                    )}
                    {survey.course && (
                      <span>
                        <i className="fas fa-book me-1"></i>
                        {survey.course.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-lg-end">
                <small className="text-white-50 text-uppercase">Overall Progress</small>
                <div className="d-flex align-items-center gap-2 mt-1">
                  <span className="display-6 fw-semibold text-white mb-0">{answerProgress}%</span>
                  <small className="text-white-50">{answeredCount}/{totalQuestions} answered</small>
                </div>
              </div>
            </div>
            {survey.description && (
              <p className="mt-3 mb-0 text-white-50">
                {survey.description}
              </p>
            )}
          </div>
        </div>

        <Row className="g-3 mb-4">
          <Col md={4}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="text-muted text-uppercase small fw-semibold">Overall Progress</div>
                <div className="d-flex justify-content-between align-items-end mt-2">
                  <span className="display-6 fw-semibold text-dark">{answerProgress}%</span>
                  <span className="text-muted small">{answeredCount}/{totalQuestions}</span>
                </div>
                <ProgressBar now={answerProgress} className="mt-3" variant="success" />
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="text-muted text-uppercase small fw-semibold">Required Questions</div>
                <div className="d-flex justify-content-between align-items-end mt-2">
                  <span className="display-6 fw-semibold text-dark">{requiredProgress}%</span>
                  <span className="text-muted small">{requiredTotals.answered}/{requiredTotals.total}</span>
                </div>
                <ProgressBar now={requiredProgress} className="mt-3" variant="warning" />
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="d-flex flex-column justify-content-between">
                <div>
                  <div className="text-muted text-uppercase small fw-semibold">Need Help?</div>
                  <p className="text-muted small mb-3">
                    Take your time and share your honest feedback. You can always return to review your answers before submitting.
                  </p>
                </div>
                <Button
                  variant="outline-success"
                  size="sm"
                  className="align-self-start"
                  onClick={() => navigate('/user/support-tickets')}
                >
                  <i className="fas fa-life-ring me-2"></i>
                  Contact Support
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Alert variant="light" className="border-0 shadow-sm mb-4">
          <div className="d-flex align-items-start">
            <i className="fas fa-smile-beam text-success me-3 mt-1"></i>
            <div>
              <strong className="text-dark d-block">We value your voice!</strong>
              <span className="text-muted small">
                Your responses help us create experiences that truly support your growth. Required questions are marked with a red asterisk.
              </span>
            </div>
          </div>
        </Alert>

        {!hasQuestions ? (
          <Alert variant="warning" className="border-0 shadow-sm">
            This survey does not have any questions yet. Please check back later.
          </Alert>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row className="justify-content-center">
              <Col lg={10}>
                {survey.questions.map((question, index) => renderQuestion(question, index))}

                <Card className="border-0 shadow-sm">
                  <Card.Body className="p-4">
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                      <div>
                        <h6 className="mb-1 text-dark">Ready to submit?</h6>
                        <small className="text-muted">You can review your answers before sending them.</small>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          size="lg"
                          onClick={handleBack}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="success"
                          type="submit"
                          size="lg"
                          disabled={submitting}
                        >
                          {submitting ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane me-2"></i>
                              Submit Survey
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Container>
    </div>
  );
};

export default TakeSurvey;

